import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  ExpandLess,
  ExpandMore,
  GroupsOutlined,
  StarRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import type { IUser } from "../../../../types/callsheet";

interface CrewRecommendation {
  crew_member_id: number;
  user_id: number | null;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone_number: string | null;
  average_rating: number | null;
  rating_count: number;
  streams_completed: number;
  streams_assigned: number;
  experience_years: number;
  completeness_score: number;
  has_conflict: boolean;
  score: number;
  reasons: string[];
}

interface RoleRequirement {
  role: { id: number; name: string };
  quantity: number;
  assigned: number;
  assigned_crew_ids: number[];
  missing: number;
  is_mandatory: boolean;
  notes: string | null;
  source: "package" | "manual";
  recommendations: CrewRecommendation[];
}

interface CrewRequirementsResponse {
  stream_id: number;
  package: { id: number; name: string } | null;
  summary: string;
  requirements: RoleRequirement[];
}

interface Props {
  streamId: number;
  /** Bump to force a refetch (e.g. after a crew member is added/removed). */
  refreshKey: number;
  onAssign: (user: IUser, roleIds: number[]) => Promise<boolean>;
}

// When the package requires these roles, their sections float to the top,
// in this order. Roles not listed keep their package order.
const ROLE_ORDER = ["PRODUCER", "EXECUTIVE PRODUCER"];

// Self-contained palette — deliberately not the app theme tokens.
const C = {
  headerBg: "#ffffff",
  headerText: "#1c1c1e",
  headerSub: "#6b6b6b",
  headerBorder: "#ececef",
  bodyBg: "#ffffff",
  cardBg: "#ffffff",
  cardBorder: "#e8e8ec",
  // Same flat tint as the call-sheet table header / tab strip.
  headingBg: "#f2f2f5",
  textPrimary: "#1f1f1f",
  textSecondary: "#5f5f5f",
  textMuted: "#8a8a8a",
  brown: "#6b4226",
  brownHover: "#573620",
  ok: "#2e9e5b",
  okChip: "#e2f1e8",
  okChipText: "#1e7a44",
  warn: "#d6533b",
  warnChipBg: "#f3ddd6",
  warnChipText: "#a83a2a",
  warnText: "#b4472f",
  chipBg: "#f0f0f2",
  chipText: "#5a5a5a",
  star: "#f5b301",
  track: "#eaeaee",
};

const recommendationToIUser = (rec: CrewRecommendation): IUser =>
  ({
    id: rec.crew_member_id,
    user: {
      id: rec.user_id ?? undefined,
      email: rec.email ?? "",
      first_name: rec.first_name || rec.full_name,
      last_name: rec.last_name || "",
      individual: { phone_number: rec.phone_number ?? "" },
    },
    average_rating: rec.average_rating ?? undefined,
    crew_member: { bio: "" },
  }) as IUser;

const orderRequirements = (reqs: RoleRequirement[]): RoleRequirement[] => {
  const rank = (name: string) => {
    const i = ROLE_ORDER.indexOf(name.toUpperCase());
    return i === -1 ? ROLE_ORDER.length : i;
  };
  return reqs
    .map((req, index) => ({ req, index }))
    .sort((a, b) => {
      const byRole = rank(a.req.role.name) - rank(b.req.role.name);
      return byRole !== 0 ? byRole : a.index - b.index;
    })
    .map((x) => x.req);
};

const CrewRecommendationsSidebar: React.FC<Props> = ({
  streamId,
  refreshKey,
  onAssign,
}) => {
  const [data, setData] = useState<CrewRequirementsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [assigningId, setAssigningId] = useState<number | null>(null);

  const fetchRequirements = useCallback(async () => {
    if (!streamId) return;
    try {
      setLoading(true);
      const res = await apiService.get<CrewRequirementsResponse>(
        `/api/automation/streams/${streamId}/crew-requirements/`,
      );
      setData(res.data);
      setExpanded((prev) => {
        const next = { ...prev };
        res.data.requirements.forEach((req) => {
          if (next[req.role.id] === undefined) {
            next[req.role.id] =
              req.missing > 0 || (req.source === "manual" && req.assigned === 0);
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Failed to load crew recommendations", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements, refreshKey]);

  // Hide package roles that are fully staffed — once every slot is filled the
  // role has nothing left to recommend, so it drops off the sidebar.
  const requirements = useMemo(
    () =>
      data
        ? orderRequirements(
            data.requirements.filter(
              (req) => !(req.source === "package" && req.missing <= 0),
            ),
          )
        : [],
    [data],
  );

  const hadRequirements = (data?.requirements.length ?? 0) > 0;

  const handleAssign = async (rec: CrewRecommendation, roleId: number) => {
    setAssigningId(rec.crew_member_id);
    try {
      const ok = await onAssign(recommendationToIUser(rec), [roleId]);
      if (ok) {
        toast.success(`${rec.full_name} assigned.`);
        await fetchRequirements();
      }
    } catch {
      // onAssign surfaces its own error toast.
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: "14px",
        backgroundColor: C.bodyBg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          backgroundColor: C.headerBg,
          borderBottom: `1px solid ${C.headerBorder}`,
          flexShrink: 0,
        }}
      >
        <GroupsOutlined fontSize="small" sx={{ color: C.headerText }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.headerText }}>
            Staffing
          </Typography>
          {data?.package && (
            <Typography sx={{ fontSize: 11, color: C.headerSub }}>
              {data.package.name}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ px: 1.5, py: 1.5 }}>
        {loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : requirements.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: C.textMuted, px: 1, py: 2 }}>
            {hadRequirements
              ? "All package roles are fully staffed."
              : "No staffing recommendations. Add crew requirements to the package on this stream to see suggestions here."}
          </Typography>
        ) : (
          requirements.map((req) => {
            const isPackage = req.source === "package";
            const filled = isPackage ? req.missing === 0 : req.assigned > 0;
            const isOpen =
              expanded[req.role.id] ??
              (req.missing > 0 || (!isPackage && req.assigned === 0));

            return (
              <Box
                key={req.role.id}
                sx={{
                  mb: 1,
                  borderRadius: "10px",
                  border: `1px solid ${C.cardBorder}`,
                  backgroundColor: C.cardBg,
                  overflow: "hidden",
                }}
              >
                {/* Role heading */}
                <Box
                  onClick={() =>
                    setExpanded((prev) => ({
                      ...prev,
                      [req.role.id]: !isOpen,
                    }))
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 1,
                    cursor: "pointer",
                    backgroundColor: C.headingBg,
                  }}
                >
                  {filled ? (
                    <CheckCircleOutline sx={{ fontSize: 16, color: C.ok }} />
                  ) : (
                    <WarningAmberRounded sx={{ fontSize: 16, color: C.warn }} />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 13,
                      color: C.textPrimary,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    {req.role.name}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {isPackage ? (
                    <Chip
                      size="small"
                      label={`${req.assigned}/${req.quantity}`}
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        backgroundColor: filled ? C.okChip : C.warnChipBg,
                        color: filled ? C.okChipText : C.warnChipText,
                      }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="optional"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        backgroundColor: C.chipBg,
                        color: C.chipText,
                      }}
                    />
                  )}
                  {isOpen ? (
                    <ExpandLess sx={{ fontSize: 18, color: C.textMuted }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: C.textMuted }} />
                  )}
                </Box>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 1.25, py: 1 }}>
                    {isPackage && !filled && (
                      <Typography sx={{ fontSize: 11, color: C.warnText, mb: 1 }}>
                        Needs {req.missing} more {req.role.name.toLowerCase()}
                        {req.missing === 1 ? "" : "s"}.
                      </Typography>
                    )}
                    {req.recommendations.length === 0 && (
                      <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                        No crew with this role found.
                      </Typography>
                    )}

                    {req.recommendations.map((rec) => {
                      // Role-scoped: only disabled if already assigned to THIS
                      // role, so one person can still be added to another role.
                      const alreadyAssigned = req.assigned_crew_ids.includes(
                        rec.crew_member_id,
                      );
                      return (
                        <Box
                          key={rec.crew_member_id}
                          sx={{
                            py: 1,
                            borderTop: `1px solid ${C.cardBorder}`,
                            "&:first-of-type": { borderTop: "none" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: C.textPrimary,
                                flexGrow: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {rec.full_name}
                            </Typography>
                            <StarRounded sx={{ fontSize: 14, color: C.star }} />
                            <Typography
                              sx={{ fontSize: 11, color: C.textSecondary }}
                            >
                              {rec.average_rating != null
                                ? `${rec.average_rating.toFixed(1)} (${rec.rating_count})`
                                : "no rating"}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              mt: 0.5,
                            }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={Math.round(rec.score * 100)}
                              sx={{
                                flexGrow: 1,
                                height: 5,
                                borderRadius: 3,
                                backgroundColor: C.track,
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor: C.ok,
                                },
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 10,
                                color: C.textMuted,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {Math.round(rec.score * 100)}% · {rec.streams_completed} str
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mt: 0.75,
                            }}
                          >
                            {rec.reasons.map((reason, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={reason}
                                icon={
                                  reason.startsWith("⚠") ? (
                                    <WarningAmberRounded
                                      sx={{ fontSize: 12, color: C.warn }}
                                    />
                                  ) : undefined
                                }
                                sx={{
                                  height: "auto",
                                  py: 0.25,
                                  fontSize: 10,
                                  backgroundColor: C.chipBg,
                                  color: C.chipText,
                                  "& .MuiChip-label": {
                                    px: 0.75,
                                    whiteSpace: "normal",
                                  },
                                }}
                              />
                            ))}
                          </Box>

                          <Button
                            fullWidth
                            size="small"
                            variant={rec.has_conflict ? "outlined" : "contained"}
                            disabled={
                              alreadyAssigned ||
                              assigningId === rec.crew_member_id
                            }
                            onClick={() => handleAssign(rec, req.role.id)}
                            sx={{
                              mt: 1,
                              textTransform: "none",
                              fontSize: 12,
                              ...(rec.has_conflict
                                ? {
                                    color: C.brown,
                                    borderColor: C.brown,
                                    "&:hover": { borderColor: C.brownHover },
                                  }
                                : {
                                    backgroundColor: C.brown,
                                    color: "#fff",
                                    "&:hover": { backgroundColor: C.brownHover },
                                  }),
                            }}
                          >
                            {alreadyAssigned
                              ? "Assigned"
                              : assigningId === rec.crew_member_id
                                ? "Assigning…"
                                : `Assign as ${req.role.name.toLowerCase()}`}
                          </Button>

                          {rec.has_conflict && (
                            <Tooltip title="Already booked on another stream this day">
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: C.warnText,
                                  mt: 0.5,
                                  textAlign: "center",
                                }}
                              >
                                Scheduling conflict
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default CrewRecommendationsSidebar;
